// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2022-11-27 17:28:25 +0100 (Sun, 27 Nov 2022) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 130903 $
//  Purpose   : wrap thirdparty components for lazy loading
// =============================================================================
// ---Third-party imports
import React, {Component, Fragment} from 'react'
//~ import Loadable from 'react-loadable'
import loadable from '@loadable/component'
import now from 'performance-now'

// ---Framework imports
import {dict, size, entries, has, includes, isArray} from 'ldash'
import {delay} from 'uix/sagas'
import log from 'uix/logging'
import {DelayedDisplay} from 'uix/components/delayedDisplay'

// ---Globals
const LOGGED = {}
const log_load = (exportedName, ts) => {
  // log load event only once
  if (exportedName) {
    if (LOGGED[exportedName]) {
      return
    } else {
      LOGGED[exportedName] = true
    }
  }
  let n = exportedName || 'defaultExport'
  log.chunk('loaded chunk: %o [%d ms]', n, now() - ts)
}

export const GetLoadable = (loader, exportedName) => {
  let opts = {
    fallback: (
      <DelayedDisplay delay={1000}>
        <div>...</div>
      </DelayedDisplay>
    ),
    ssr: false,
  }

  let l = loadable(() => {
    let imp = loader()
    let ts = now()
    return new Promise(function(resolve, reject) {
      imp
        .then(loaded => {
          log_load(exportedName, ts)
          resolve(exportedName ? loaded[exportedName] : loaded)
        })
        .catch(err => {
          reject(err)
        })
    })
  }, opts)

  return l
}

export const GetLoadables = (loader, exportedNames) =>
  dict(exportedNames.map(n => [n, GetLoadable(loader, n)]))

export const UtilityChunks = {TreeUtils: []}
const UtilityChunkLoaders = {}

export let registerChunk = (name, exported) => {
  UtilityChunks[name] = exported.names
  UtilityChunkLoaders[name] = exported.loader
}

export const isUtilityChunkLoaded = name => !isArray(UtilityChunks[name])
export const getUtilityChunk = name => {
  if (name === 'ALL') return UtilityChunks

  if (isArray(UtilityChunks[name])) {
    throw Error(`${name} not loaded`)
  }
  return UtilityChunks[name]
}

export function* loadUtilityChunks(chunkNames) {
  if (chunkNames.every(n => isUtilityChunkLoaded(n))) return

  let loaded = {}
  let load = (importPromise, name, ts) =>
    importPromise
      .then(module => {
        try {
          let exportedNames = UtilityChunks[name]
          let exportedUtils = dict(exportedNames.map(n => [n, module[n]]))
          let unResolved = entries(exportedUtils).filter(e => e[1] === undefined)
          if (unResolved.length > 0) {
            log.error('unresolved exported names: %o %o', unResolved.map(e => e[0]), module)
          }

          loaded[name] = exportedUtils
          log.chunk('loaded %o utils from chunk %o [%d ms]', size(exportedUtils), name, now() - ts)
        } catch (err) {
          log.error('error fetching chunk %o %O', name, err)
          loaded[name] = false
        }
      })
      .catch(err => {
        log.error('load failure for chunk %o %O', name, err)
        loaded[name] = false
      })

  let promises = []

  // #if process.env.UIX_CHUNKED === 'true'
  for (let n of chunkNames) {
    promises.push(load(UtilityChunkLoaders[n](), n, now()))
  }
  // #endif

  //~ log.debug('waiting for %o chunks', size(promises))
  while (size(loaded) < size(promises)) {
    yield delay(5)
  }

  for (let [name, data] of entries(loaded).filter(e => e[1])) {
    UtilityChunks[name] = data // load errors are flagged as "false"
  }
}
